import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Search, Mountain, ClipboardCheck, ArrowRight } from 'lucide-react';
type Step = {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
};
const steps: Step[] = [{
  number: '1',
  title: 'Connection Call',
  description: 'Start the conversation',
  icon: <MessageCircle size={24} strokeWidth={1.5} data-id="element-89" data-mp="plphh4cqzn1q" />
}, {
  number: '2',
  title: 'Life Scan',
  description: 'See where you are now',
  icon: <Search size={24} strokeWidth={1.5} data-id="element-90" data-mp="ujhay2t1jh81" />
}, {
  number: '3',
  title: 'Future Focus',
  description: 'Define where you want to go',
  icon: <Mountain size={24} strokeWidth={1.5} data-id="element-91" data-mp="kmmavfh4d8rn" />
}, {
  number: '4',
  title: 'Clarity Roadmap',
  description: 'Create your next steps',
  icon: <ClipboardCheck size={24} strokeWidth={1.5} data-id="element-92" data-mp="31agriwv6uxas" />
}];
export function JourneySection() {
  return <section className="relative py-24 overflow-hidden" data-id="element-93" data-mp="35eeq96glq63b">
      <div className="absolute inset-0 bg-grain opacity-15 pointer-events-none mix-blend-overlay" data-id="element-94" data-mp="2zfbtu3f7joct"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-95" data-mp="2uh0fbfdywpo9">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-96" data-mp="38g02eilu0wib">
          <p className="text-cream-50/90 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mb-8" data-id="element-97" data-mp="3df2yvenqxueo">
            Mark created a 4-step coaching model, based on ICF principles, to
            help you step back from the pressure of daily life, regain ownership
            of your choices, and create your own direction.
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-gold-400 mb-4 text-glow" data-id="element-98" data-mp="gcntkddw80">
            Your 4-Step Journey
          </h2>
          <div className="flex items-center justify-center gap-2 mb-5" data-id="element-99" data-mp="2tthegjn1j58w">
            <span className="h-px w-12 bg-gold-400/40" data-id="element-100" data-mp="2yr9g45dhm5qa"></span>
            <span className="w-1.5 h-1.5 rounded-full bg-gold-400/60" data-id="element-101" data-mp="2ypewcudycjns"></span>
            <span className="h-px w-12 bg-gold-400/40" data-id="element-102" data-mp="32sbb2a0dfvmg"></span>
          </div>
          <p className="text-cream-50/80 text-lg max-w-2xl mx-auto" data-id="element-103" data-mp="37k2zadw21p9f">
            Move from feeling stuck to clarity and direction.
          </p>
        </motion.div>

        {/* Horizontal Flowchart - scrolls on small screens */}
        <div className="overflow-x-auto pb-6 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide" data-id="element-104" data-mp="3hgfwhlqt6l8x">
          <div className="flex flex-row items-center justify-start lg:justify-center gap-2 sm:gap-3 min-w-max lg:min-w-0" data-id="element-105" data-mp="3mieush7oyrz0">
            {/* Starting Point A */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="flex flex-col items-center gap-2 flex-shrink-0 w-[90px]" data-id="element-106" data-mp="1s3kqcyox5lid">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-night-800 border-2 border-gold-400/40 flex items-center justify-center font-serif text-xl sm:text-2xl text-gold-400" data-id="element-107" data-mp="1et924agtqnbn">
                A
              </div>
              <span className="text-cream-50/70 text-xs sm:text-sm font-medium text-center" data-id="element-108" data-mp="22l66jghy2d2q">
                Feeling stuck
              </span>
            </motion.div>

            {/* Dotted connector */}
            <div className="flex items-center self-center pt-0 sm:-mt-7 flex-shrink-0" data-id="element-109" data-mp="1tkriwahiclw2">
              <div className="w-4 sm:w-6 border-t-2 border-dotted border-gold-400/40" data-id="element-110" data-mp="n3waleva1jx0"></div>
            </div>

            {/* Steps with arrows between */}
            {steps.map((step, index) => <Fragment key={step.number} data-id="element-111" data-mp="2620wdfi8trt4" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1 + index * 0.15,
              ease: 'easeOut'
            }} className="relative flex-shrink-0 w-[180px] sm:w-[200px] lg:w-[210px]" data-id="element-112" data-mp="2607pq07mr7l4">
                  {/* Numbered Badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20" data-id="element-113" data-mp="2kqezzsnhhkaq">
                    <div className="w-9 h-9 rounded-full bg-gold-400 text-night-900 font-serif font-bold flex items-center justify-center shadow-lg" data-id="element-114" data-mp="2kshvutbbi7nd">
                      {step.number}
                    </div>
                  </div>

                  {/* Card */}
                  <div className="h-full bg-night-800/50 backdrop-blur-sm border border-gold-400/20 rounded-2xl pt-8 pb-5 px-4 flex flex-col items-center text-center transition-all duration-300 hover:border-gold-400/50 hover:bg-night-800/70 hover:-translate-y-1" data-id="element-115" data-mp="10swq1gjq5kek">
                    <div className="w-12 h-12 rounded-full bg-gold-400/10 border border-gold-400/30 flex items-center justify-center text-gold-400 mb-3" data-id="element-116" data-mp="15taygzp8mwd4">
                      {step.icon}
                    </div>
                    <h3 className="font-serif text-lg sm:text-xl text-cream-50 mb-2" data-id="element-117" data-mp="d5yfeziad6ok">
                      {step.title}
                    </h3>
                    <div className="w-8 h-px bg-gold-400/40 mb-2" data-id="element-118" data-mp="rvmgbzmk23br"></div>
                    <p className="text-cream-50/70 text-xs sm:text-sm leading-relaxed" data-id="element-119" data-mp="n1i34ymp4z28">
                      {step.description}
                    </p>
                  </div>
                </motion.div>

                {/* Arrow between cards (not after last) */}
                {index < steps.length - 1 && <motion.div initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: 0.25 + index * 0.15
            }} className="flex items-center self-center flex-shrink-0" data-id="element-120" data-mp="1ueki3x9dyu4o">
                    <ArrowRight size={20} className="text-gold-400/70" strokeWidth={1.5} data-id="element-121" data-mp="1khzcpatgfh0b" />
                  </motion.div>}
              </Fragment>)}

            {/* Arrow before B */}
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.85
          }} className="flex items-center self-center flex-shrink-0" data-id="element-122" data-mp="30w7n670y9zvl">
              <ArrowRight size={20} className="text-gold-400" strokeWidth={1.5} data-id="element-123" data-mp="2x8hotjuu7k2l" />
            </motion.div>

            {/* Ending Point B */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.95
          }} className="flex flex-col items-center gap-2 flex-shrink-0 w-[90px]" data-id="element-124" data-mp="2nabj0r5wttsg">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gold-400 border-2 border-gold-400 flex items-center justify-center font-serif text-xl sm:text-2xl text-night-900 font-bold shadow-[0_0_20px_rgba(244,196,48,0.4)]" data-id="element-125" data-mp="vt35gt9k2g74">
                B
              </div>
              <span className="text-gold-400 text-xs sm:text-sm font-medium text-center" data-id="element-126" data-mp="r0a2oaxndhxo">
                Clarity & direction
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}